import type { TransactionData } from '@shared/types';
import type { BackendRouteDefinition } from '../types';

export interface FinancialTransactionMutationResponse {
  allocationStatus: 'preserved' | 'released' | 'unaffected';
  status: 'created' | 'ignoredStaleProviderUpdate' | 'updated';
  transactionId: string;
}

export const createFirmTransactionRoute = {
  body: (data: TransactionData & { idempotencyKey?: string }) => data,
  method: 'POST',
  path: '/firms/transactions',
  transport: 'api',
} as const satisfies BackendRouteDefinition;

export const updateFirmTransactionRoute = {
  body: (request: { data: Record<string, unknown>; transactionId: string }) =>
    request.data,
  method: 'PATCH',
  params: (request: { transactionId: string }) => ({
    transactionId: request.transactionId,
  }),
  path: '/firms/transactions/:transactionId',
  transport: 'api',
} as const satisfies BackendRouteDefinition;

export const failUnconsumedRetainerTransferRoute = {
  method: 'POST',
  params: (request: { transactionId: string }) => ({
    transactionId: request.transactionId,
  }),
  path: '/firms/transactions/:transactionId/fail-unconsumed-retainer-transfer',
  transport: 'api',
} as const satisfies BackendRouteDefinition;

export const refreshUnconsumedRetainerTransferRoute = {
  body: (request: { data: Record<string, unknown>; transactionId: string }) =>
    request.data,
  method: 'PATCH',
  params: (request: { transactionId: string }) => ({
    transactionId: request.transactionId,
  }),
  path: '/firms/transactions/:transactionId/refresh-unconsumed-retainer-transfer',
  transport: 'api',
} as const satisfies BackendRouteDefinition;

export const deleteFirmTransactionRoute = {
  method: 'DELETE',
  params: (request: { transactionId: string }) => ({
    transactionId: request.transactionId,
  }),
  path: '/firms/transactions/:transactionId',
  transport: 'api',
} as const satisfies BackendRouteDefinition;

export const classifyLateHistoricalTransactionRoute = {
  method: 'POST',
  params: (request: { transactionId: string }) => ({
    transactionId: request.transactionId,
  }),
  path: '/firms/transactions/:transactionId/classify-late-historical',
  transport: 'api',
} as const satisfies BackendRouteDefinition;

export const unlinkFirmFinancialInstitutionRoute = {
  body: (request: {
    deleteTransactions: boolean;
    isFinalChunk: boolean;
    transactionIds: string[];
    financialInstitutionId: string;
  }) => ({
    deleteTransactions: request.deleteTransactions,
    isFinalChunk: request.isFinalChunk,
    transactionIds: request.transactionIds,
  }),
  method: 'POST',
  params: (request: { financialInstitutionId: string }) => ({
    financialInstitutionId: request.financialInstitutionId,
  }),
  path: '/firms/financial-institutions/:financialInstitutionId/unlink',
  transport: 'api',
} as const satisfies BackendRouteDefinition;

export const adjustMatterInvoiceBalanceRoute = {
  body: (request: {
    amountCents: number;
    expectedRevision: number;
    matterId: string;
    operationId: string;
    reason: string;
  }) => ({
    amountCents: request.amountCents,
    expectedRevision: request.expectedRevision,
    operationId: request.operationId,
    reason: request.reason,
  }),
  method: 'POST',
  params: (request: { matterId: string }) => ({ matterId: request.matterId }),
  path: '/firms/matters/:matterId/invoice-balance-adjustments',
  transport: 'api',
} as const satisfies BackendRouteDefinition;

export const getMatterInvoiceBalanceRoute = {
  method: 'GET',
  params: (request: { matterId: string }) => ({ matterId: request.matterId }),
  path: '/firms/matters/:matterId/invoice-balance',
  transport: 'api',
} as const satisfies BackendRouteDefinition;

export const getMatterAmountDueRoute = {
  method: 'GET',
  params: (request: { matterId: string }) => ({ matterId: request.matterId }),
  path: '/firms/matters/:matterId/amount-due',
  query: (request: { finixEnvironment: 'live' | 'sandbox' }) => ({
    finixEnvironment: request.finixEnvironment,
  }),
  transport: 'api',
} as const satisfies BackendRouteDefinition;
